import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useAssistantStore } from "@/stores/assistantStore";
import { useTeamStore } from "@/stores/teamStore";
import { useAuthStore } from "@/stores/authStore";
import { useUIStore } from "@/stores/uiStore";
import type { Message } from "@/stores/assistantStore";
import { approveHarnessAction } from "@/api/assistant";
import { renderMarkdownHtml } from "@/components/common/SafeHtml";
import { normalizeAssistantDisplayName } from "@/lib/branding";

function RenderedMarkdown({ content }: { content: string }) {
  const html = useMemo(() => renderMarkdownHtml(content), [content]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const preBlocks = container.querySelectorAll("pre");
    preBlocks.forEach((pre) => {
      if (pre.querySelector(".assistant-code-copy-control")) return;

      pre.style.position = "relative";
      const btn = document.createElement("button");
      btn.className = "btn btn--ghost btn--sm assistant-code-copy-control";
      btn.textContent = "Copy";
      btn.addEventListener("click", () => {
        const code = pre.querySelector("code");
        const text = (code || pre).textContent || "";
        navigator.clipboard.writeText(text).then(() => {
          btn.textContent = "Copied!";
          setTimeout(() => {
            btn.textContent = "Copy";
          }, 2000);
        });
      });
      pre.appendChild(btn);
    });
  }, [html]);

  return (
    <div
      ref={containerRef}
      className="assistant-markdown"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function formatToolName(name: string): string {
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

type DisplayToolCall = NonNullable<Message["toolCalls"]>[number] & {
  count: number;
};

const HIDDEN_TOOL_NAMES = new Set(["query_data"]);

function getDisplayToolCalls(toolCalls?: Message["toolCalls"]): DisplayToolCall[] {
  if (!toolCalls?.length) return [];

  const displayTools: DisplayToolCall[] = [];
  for (const tool of toolCalls) {
    const shouldHide =
      HIDDEN_TOOL_NAMES.has(tool.name) && !tool.approvalId && !tool.runId;
    if (shouldHide) continue;

    const lastTool = displayTools[displayTools.length - 1];
    if (
      lastTool &&
      lastTool.name === tool.name &&
      !lastTool.approvalId &&
      !lastTool.runId &&
      !tool.approvalId &&
      !tool.runId
    ) {
      lastTool.count += 1;
      lastTool.result = tool.result ?? lastTool.result;
    } else {
      displayTools.push({ ...tool, count: 1 });
    }
  }

  return displayTools;
}

function BotAvatar({ avatarUrl }: { avatarUrl?: string | null }) {
  if (avatarUrl) {
    return (
      <div className="assistant-avatar assistant-avatar--bot">
        <img src={avatarUrl} alt="AI" referrerPolicy="no-referrer" />
      </div>
    );
  }
  return (
    <div className="assistant-avatar assistant-avatar--bot">
      <svg
        viewBox="0 0 24 24"
        width="12"
        height="12"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
      </svg>
    </div>
  );
}

function MessageBubble({
  message,
  userInitials,
  userPicture,
  aiName,
  aiAvatarUrl,
  teamId,
  isPending = false,
}: {
  message: Message;
  userInitials: string;
  userPicture?: string | null;
  aiName?: string;
  aiAvatarUrl?: string | null;
  teamId?: string;
  isPending?: boolean;
}) {
  const isUser = message.role === "user";
  const showPendingState = !isUser && isPending;
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const displayToolCalls = getDisplayToolCalls(message.toolCalls);

  async function handleApprove(approvalId: string) {
    if (!teamId) return;
    setApprovingId(approvalId);
    try {
      await approveHarnessAction(teamId, approvalId);
    } finally {
      setApprovingId(null);
    }
  }

  return (
    <div
      className={`assistant-message ${isUser ? "assistant-message--user" : "assistant-message--assistant"}`}
    >
      <div
        className={`assistant-message__bubble${showPendingState ? " assistant-message__bubble--thinking" : ""}`}
      >
        {/* Header with avatar */}
        <div className="assistant-message__header">
          {isUser ? (
            <div className="assistant-avatar assistant-avatar--user">
              {userPicture ? (
                <img src={userPicture} alt="You" referrerPolicy="no-referrer" />
              ) : (
                <span>{userInitials}</span>
              )}
            </div>
          ) : (
            <BotAvatar avatarUrl={aiAvatarUrl} />
          )}
          <span className="assistant-message__sender">
            {isUser ? "You" : aiName || "AI"}
          </span>
          <span className="assistant-message__time">
            {message.timestamp.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>

        {/* Tool calls */}
        {displayToolCalls.length > 0 && (
          <div className="assistant-message__tools">
            {displayToolCalls.map((tool, idx) => (
              <div key={idx} className="assistant-tool-call">
                <svg
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>
                  {formatToolName(tool.name)}
                  {tool.count > 1 ? ` ×${tool.count}` : ""}
                </span>
                {tool.runId && (
                  <a className="assistant-tool-call__link" href={`#/engineering/agents?run=${tool.runId}`}>
                    Open
                  </a>
                )}
                {tool.approvalId && (
                  <button
                    type="button"
                    className="assistant-tool-call__button"
                    onClick={() => handleApprove(tool.approvalId!)}
                    disabled={!teamId || approvingId === tool.approvalId}
                  >
                    {approvingId === tool.approvalId ? "Approving" : "Approve"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Message content */}
        {showPendingState ? (
          <div className="assistant-thinking">
            <div className="assistant-thinking__dots" aria-label="Thinking">
              <span className="assistant-thinking__dot" />
              <span className="assistant-thinking__dot" />
              <span className="assistant-thinking__dot" />
            </div>
          </div>
        ) : (
          <div className="assistant-message__content">
            {isUser ? (
              message.content
                .split("\n")
                .map((line, i) => <p key={i}>{line || "\u00A0"}</p>)
            ) : (
              <RenderedMarkdown content={message.content} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function AssistantPanel() {
  const isOpen = useAssistantStore((state) => state.isOpen);
  const messages = useAssistantStore((state) => state.messages);
  const isLoading = useAssistantStore((state) => state.isLoading);
  const open = useAssistantStore((state) => state.open);
  const close = useAssistantStore((state) => state.close);
  const sendMessage = useAssistantStore((state) => state.sendMessage);
  const hydrateConversation = useAssistantStore(
    (state) => state.hydrateConversation,
  );
  const isStreaming = useAssistantStore((state) => state.isStreaming);
  const clearConversation = useAssistantStore(
    (state) => state.clearConversation,
  );
  const currentTeam = useTeamStore((state) => state.currentTeam);
  const user = useAuthStore((state) => state.user);
  const activeTab = useUIStore((state) => state.activeTab);

  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // User initials for avatar
  const userInitials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) ||
    user?.email?.charAt(0).toUpperCase() ||
    "U";

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !currentTeam) return;
    hydrateConversation(currentTeam.id).catch(() => {
      // Hydration failures are non-blocking.
    });
  }, [isOpen, currentTeam, hydrateConversation]);

  const handleSubmit = async () => {
    if (!input.trim() || isLoading || isStreaming || !currentTeam) return;

    const message = input.trim();
    setInput("");

    try {
      await sendMessage(currentTeam.id, message);
    } catch {
      // Error handled in store
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleClear = async () => {
    if (!currentTeam) return;
    await clearConversation(currentTeam.id);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const aiName = normalizeAssistantDisplayName(currentTeam?.ai_name);
  const aiAvatarUrl = currentTeam?.ai_avatar_url || undefined;

  // Get contextual suggestions based on active tab
  const PAGE_SUGGESTIONS: Record<string, string[]> = {
    okendo: [
      "What are the top issues in negative reviews?",
      "Show me this week's review summary",
      "Which products need attention?",
      "What do customers love most about our top products?",
    ],
    "team-priorities": [
      "What are the top company themes this week?",
      "Which departments are overloaded?",
      "What should leadership prioritize next week?",
    ],
    home: [
      "What leadership pages should I open first today?",
      "Summarize my Notion task board",
      "What should I review first today?",
    ],
    boards: [
      "How do boards map Notion task statuses?",
      "What columns should we use for initiatives?",
    ],
    coaching: [
      "What coaching themes are we tracking?",
      "How is this different from Gorgias agent coaching?",
    ],
    learning: [
      "What learning modules should we prioritize?",
      "Where will course links come from when wired?",
    ],
    "daily-ritual": [
      "What should I include in my daily ritual?",
      "How should I close out today?",
    ],
    gorgias: [
      "Summarize recent ticket trends",
      "What are the top complaint categories?",
    ],
    jobs: [
      "Show me all active scheduled jobs",
      "Create a weekly digest for reviews",
      "What jobs ran today?",
    ],
    gtm: [
      "What products are launching this week?",
      "Show me the upcoming launch calendar",
    ],
    email: [
      "What campaigns are currently active in Klaviyo?",
      "Which flows should we prioritize updating this week?",
    ],
    pricing: [
      "How do our prices compare to competitors?",
      "Which products have the best margins?",
    ],
    dashboard: [
      "Give me a high-level summary of this week",
      "What are the key trends across all data?",
      "Any urgent items I should know about?",
    ],
  };

  const DEFAULT_SUGGESTIONS = [
    "What are the top issues in negative reviews?",
    "Show me this week's review summary",
    "Which products need attention?",
  ];

  const suggestions = PAGE_SUGGESTIONS[activeTab] || DEFAULT_SUGGESTIONS;
  const lastMessage = messages[messages.length - 1];

  // Collapsed state - thin sidebar with open button
  if (!isOpen) {
    return (
      <div className="assistant-collapsed">
        <button
          className="assistant-collapsed__button"
          onClick={open}
          title={`Open ${aiName}`}
        >
          {aiAvatarUrl ? (
            <img
              src={aiAvatarUrl}
              alt={aiName || "AI"}
              referrerPolicy="no-referrer"
            />
          ) : (
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
              <path d="M5 19l.5 1.5L7 21l-1.5.5L5 23l-.5-1.5L3 21l1.5-.5L5 19z" />
              <path d="M19 13l.5 1.5L21 15l-1.5.5L19 17l-.5-1.5L17 15l1.5-.5L19 13z" />
            </svg>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="assistant-panel">
      {/* Header */}
      <div className="assistant-panel__header">
        <div className="assistant-panel__title">
          <div
            className={`assistant-panel__icon${aiAvatarUrl ? " assistant-panel__icon--avatar" : ""}`}
          >
            {aiAvatarUrl ? (
              <img
                src={aiAvatarUrl}
                alt={aiName || "AI"}
                referrerPolicy="no-referrer"
              />
            ) : (
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
              </svg>
            )}
          </div>
          <span>{aiName}</span>
        </div>
        <div className="assistant-panel__actions">
          {messages.length > 0 && (
            <button
              type="button"
              className="icon-button icon-button--inverse icon-button--compact"
              onClick={handleClear}
              title="Clear conversation"
              aria-label="Clear conversation"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M8 16H3v5" />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="icon-button icon-button--inverse icon-button--compact"
            onClick={close}
            title="Close panel"
            aria-label="Close panel"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M9 3v18" />
              <path d="M14 9l3 3-3 3" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="assistant-panel__messages">
        {messages.length === 0 ? (
          <div className="assistant-panel__empty">
            <div className="assistant-panel__empty-icon">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
              </svg>
            </div>
            <h4>{aiName ? `Hi, I'm ${aiName}!` : "How can I help?"}</h4>
            <p>
              I can explore your data, generate reports, and answer questions
              about your reviews.
            </p>
            <div className="assistant-panel__suggestions">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                userInitials={userInitials}
                userPicture={user?.picture}
                aiName={aiName}
                aiAvatarUrl={aiAvatarUrl}
                teamId={currentTeam?.id}
                isPending={
                  isStreaming &&
                  message.id === lastMessage?.id &&
                  message.role === "assistant" &&
                  !message.content.trim()
                }
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="assistant-panel__input">
        <div className="assistant-panel__input-row">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              currentTeam ? "Ask me anything..." : "Select a team first"
            }
            disabled={!currentTeam || isLoading || isStreaming}
            rows={1}
          />
          <button
            onClick={handleSubmit}
            disabled={!input.trim() || isLoading || isStreaming || !currentTeam}
            title="Send message"
          >
            {isLoading || isStreaming ? (
              <svg
                className="assistant-send-spinner"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  strokeDasharray="31.4"
                  strokeDashoffset="10"
                />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            )}
          </button>
        </div>
        <p className="assistant-panel__hint">Press Enter to send</p>
      </div>
    </div>
  );
}

export default AssistantPanel;
